<template>
  <div class="container">
    <div class="body">
      <!-- Add Asset Dropdown (Sticky) -->
      <div class="sticky-header d-flex gap-2 mb-4">
        <div style="flex: 1">
          <AssetsTemplatesDropdown
            ref="dropdownRef"
            :item="assetSelector"
            mode="assets"
            @itemAdded="handleAssetSelected" />
        </div>

        <v-btn
          @click="addSelectedAsset"
          :disabled="!assetSelector.value"
          prepend-icon="mdi-plus-circle"
          variant="flat"
          color="primary">
          Add
        </v-btn>
      </div>

      <!-- Member Assets List (Scrollable) -->
      <div class="scrollable-content">
        <!-- Display mixed assets and groups -->
        <div
          v-if="groupAssets.length > 0 && deluge && deluge.tags"
          style="display: flex; flex-direction: column; gap: 8px">
          <WideCard
            v-for="item in groupAssets"
            :key="item.uid"
            @click="handleItemClick(item)"
            :dense="true"
            :tag="true"
            :loading="item.type === 'asset' ? item.ready !== true : false"
            :img="
              item.type === 'asset_group'
                ? item.preview_static_small
                : assetSmallThumbnail(item)
            "
            :vid="
              item.type === 'asset_group'
                ? item.preview_motion_small
                : assetSmallThumbnail(item, 'motion')
            "
            :title="item.name"
            :icon="
              item.type === 'asset_group'
                ? 'mdi-folder-multiple'
                : iconByType(item.asset_type)
            "
            :selected="false"
            :icons="
              item.type === 'asset_group'
                ? [{ icon: 'mdi-folder-multiple', color: 'orange' }]
                : getAssetIcons(item)
            "
            :tooltip="
              item.type === 'asset_group'
                ? `Nested Group: ${item.asset_count} asset(s)`
                : 'Open asset details'
            "
            :NDIName="item.asset_type == 'ndi' ? item.value : null">
            <!-- Tags (for assets only) -->
            <Tag
              v-if="item.type === 'asset'"
              v-for="(tag, i) in item.tags"
              :key="(tag, i)"
              :color="get_color_safe(deluge.tags[tag])"
              :icon="deluge.tags[tag]?.icon"
              :name="get_name_safe(deluge.tags[tag])" />

            <!-- Asset count chip (for groups) -->
            <v-chip
              v-if="item.type === 'asset_group'"
              size="small"
              color="orange"
              class="ml-2">
              {{ item.asset_count }} asset(s)
            </v-chip>

            <!-- Primary badge -->
            <v-chip
              v-if="item.is_primary"
              size="x-small"
              color="primary"
              class="ml-2">
              Primary
            </v-chip>

            <!-- Action buttons -->
            <template v-slot:actions>
              <v-btn
                v-if="item.type === 'asset' && !item.is_primary"
                @click.stop="setPrimary(item.uid)"
                icon="mdi-star"
                variant="text"
                size="small"
                title="Set as primary" />

              <v-btn
                @click.stop="removeItem(item.uid, item.type)"
                icon="mdi-close-circle"
                variant="text"
                size="small"
                color="error"
                title="Remove from group" />
            </template>
          </WideCard>
        </div>

        <!-- Empty state -->
        <v-alert
          v-else
          type="info"
          variant="tonal"
          class="mt-4">
          No assets or groups in this group yet. Use the dropdown above to add
          items.
        </v-alert>
      </div>
      <!-- End scrollable-content -->
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { watch } from 'vue';
import { $http } from '../../../stores/http';
import { deluge } from '../../../stores/deluge';
import AssetsTemplatesDropdown from '../../Shared/AssetsTemplatesDropdown.vue';
import WideCard from '../../Shared/WideCard.vue';
import Tag from '../../Shared/Tag.vue';
import { iconByType } from '../../../utils/assetTypeToMDIIcon.js';
import { assetSmallThumbnail } from '../../../utils/thumbnails.js';

const props = defineProps(['userEditObj']);
const emit = defineEmits(['request-save', 'asset-changed', 'open-asset']);

const groupAssets = ref([]);
const dropdownRef = ref(null);

// Asset selector object for AssetsTemplatesDropdown
const assetSelector = ref({
  value: '',
  label: 'Search and select asset or group to add',
  type: 'asset',
});

// Load group assets
const loadGroupAssets = async (groupUid) => {
  try {
    const targetUid = groupUid || props.userEditObj?.uid;
    if (!targetUid) return;

    const response_raw = await $http('GET', `/asset_group/${targetUid}`);
    const response = JSON.parse(response_raw);
    groupAssets.value = response.group?.assets || [];
  } catch (error) {
    console.error('Failed to load group assets:', error);
  }
};

watch(
  () => props.userEditObj?.uid,
  (newUid) => {
    if (!newUid) return;
    loadGroupAssets(newUid);
  },
  { immediate: true }
);

// Handle asset selection from dropdown
const handleAssetSelected = (assetUid, item) => {
  console.log('Asset selected:', assetUid);
  // AssetSelector.value is already set by the dropdown component
};

// Set primary asset
const setPrimary = async (assetUid) => {
  try {
    await $http(
      'PUT',
      '/set_primary_asset',
      JSON.stringify({
        asset_uid: assetUid,
      })
    );
    await loadGroupAssets();
    emit('asset-changed');
  } catch (error) {
    console.error('Failed to set primary asset:', error);
  }
};

// Remove asset from group
const removeAsset = async (assetUid) => {
  try {
    await $http(
      'PUT',
      '/remove_asset_from_group',
      JSON.stringify({
        asset_uid: assetUid,
      })
    );
    await loadGroupAssets();
    emit('asset-changed');
  } catch (error) {
    console.error('Failed to remove asset:', error);
  }
};

// Add selected asset to group
const addSelectedAsset = async () => {
  if (!assetSelector.value.value) return;

  // Prevent adding group to itself
  if (assetSelector.value.value === props.userEditObj.uid) {
    console.warn('Cannot add asset group to itself');
    alert('Cannot add an asset group to itself!');
    return;
  }

  try {
    await $http(
      'PUT',
      '/add_asset_to_group',
      JSON.stringify({
        group_uid: props.userEditObj.uid,
        asset_uid: assetSelector.value.value,
      })
    );
    await loadGroupAssets();

    // Clear selection and reset search (no direct DOM access)
    assetSelector.value = {
      value: '',
      label: 'Search and select asset or group to add',
      type: 'asset',
    };

    emit('asset-changed');
  } catch (error) {
    console.error('Failed to add asset to group:', error);
    alert(`Failed to add asset: ${error.message || 'Unknown error'}`);
  }
};

// Open asset in edit modal
const openAsset = (asset) => {
  // Emit event to parent to open EditAssetModal
  emit('open-asset', asset);
};

onMounted(() => {
  loadGroupAssets();
});
const handleItemClick = (item) => {
  if (item.type === 'asset') {
    openAsset(item);
  }
};

function get_color_safe(tag) {
  return tag?.color || 'primary';
}

function get_name_safe(tag) {
  return tag?.name || '';
}

function getAssetIcons(item) {
  const icons = [];

  if (item.ingest_src) {
    icons.push({ icon: 'mdi-filter-check' });
  }

  if (item.conditions?.play_conditions?.length) {
    icons.push({ icon: 'mdi-motion-play-outline', color: 'primary' });
  }

  if (item.extra_info?.conversion_in_progress) {
    icons.push({ icon: 'mdi-lightning-bolt', color: 'grey' });
  } else if (item.extra_info?.optimizations?.length) {
    icons.push({ icon: 'mdi-lightning-bolt', color: 'primary' });
  }

  return icons;
}
</script>

<style scoped>
.sticky-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background: rgb(var(--v-theme-surface));
  padding-bottom: 8px;
  margin-bottom: 0 !important;
}

.scrollable-content {
  max-height: calc(100vh - 350px);
  overflow-y: auto;
  padding-top: 8px;
}
</style>
